import { type ClientRequestOptions, hc, type InferRequestType, type InferResponseType } from "hono/client";
import type { App } from "./handlers";

//============================================
// CAREFUL!!!
// ONLY export types from here. You will not be able to
// build the frontend if you do not
//============================================

export type {
  ActionRecord,
  AssignableRole,
  BrandCreator,
  BrandCreatorClassificationEnum,
  ColumnLayout,
  CreatorClassificationEnum,
  CreatorCohort,
  CreatorTag,
  CreatorTagSource,
  Feature,
  Flag,
  FlagEdit,
  Hook,
  InvitationStatus,
  LarkWebhookClient,
  MembershipRole,
  MerchantOnboardingStatus,
  NbaAction,
  Policy,
  ProductStatus,
  Report,
  ReportLayoutType,
  RiskModelType,
  Role,
  SafetyPolicyType,
  SequenceTriggerType,
  SequenceType,
  TriggerConfig,
  TtsRegion,
  User,
  WorkspaceRole,
} from "@unbox/db";
export type { BrandCreatorWithDetails } from "./resources/admin/brandCreators/repo";
export type { AnswerPhase, Suggestion, Visualization, VisualizationType } from "./resources/ask/agent/agent";
export type { Timing } from "./resources/ask/agent/timing";
export type { AskQueryEvent } from "./resources/ask/events";
export type { GlossaryFamily, GlossaryTerm } from "./resources/ask/knowledge/glossary";
export type { AgentStep, ConversationMessage, DataFreshness, QueryResponse } from "./resources/ask/repo";
export type { GenerateResponse, RenderedSection, ReportProgress, ReportSSEEvent } from "./resources/ask/reports/report";
export type { SafetyMessageOverride } from "./resources/assurance/actions/repo";
export {
  ACTION_COMPLETED,
  ACTION_IN_PROGRESS,
  ACTION_NO_ACTION,
  MANUAL_REVIEW_ACTION_TYPES,
  MANUALLY_CONFIRMABLE_STATUSES,
  NO_RISKS_CONFIRMATION_UNDONE,
  NO_RISKS_CONFIRMED,
  RESOLVING_ACTION_TYPES,
} from "./resources/assurance/actionTypes";
export type {
  AssuranceSort,
  FlagWithEdits,
  SafetyVideoCategories,
  SafetyVideoStatus,
  VideoWithFlags,
} from "./resources/assurance/videos/repo";
export type { CreatorBlock } from "./resources/blocklist/blocks";
export {
  type Condition,
  type ConditionGroup,
  FIELD_DEFINITIONS,
  type FieldId,
  type Operator,
  type RuleConditions,
  ruleConditionsSchema,
} from "./resources/blocklist/conditions";
export type { BlocklistImportPreviewRow, BlocklistImportStatus } from "./resources/blocklist/routes";
export {
  COHORT_FIELD_DEFINITIONS,
  type CohortCondition,
  type CohortConditionGroup,
  type CohortConditionRange,
  type CohortConditionSummary,
  type CohortCreatorSignals,
  type CohortFieldId,
  type CohortRuleConditions,
  cohortRuleConditionsSchema,
} from "./resources/creatorCohorts/conditions";
export type { CreatorCohortAssignment, CreatorCohortWithCount } from "./resources/creatorCohorts/repo";
export type { CreatorListTemplate } from "./resources/creatorLists/repo";
export { CREATOR_TAG_COLORS, type CreatorTagColor } from "./resources/creatorTags/colors";
export type { CreatorTagAssignment, CreatorTagWithCount } from "./resources/creatorTags/repo";
export type { ProductSort } from "./resources/explorers/repo";
export type { CreatorMetricsSort, VideoMetricsSort } from "./resources/explorers/routes";
export type { JobKind } from "./resources/jobs/kinds";
export type { MerchantDraftData, MerchantDraftPermissionAnswer } from "./resources/merchantDrafts/repo";
export type {
  ChartDataPoint,
  ReportWithChartData,
  RevenueSource,
  RevenueSourceSeries,
  TableRow,
  TimeSeriesData,
} from "./resources/reports/types";
export type { AppliedBy, RuleConditionsByArea, RuleSummary } from "./resources/rules/applied-by";
export { RULE_AREAS, type RuleArea } from "./resources/rules/constants";
export { COLLAB_TRIGGER_TYPES, type CollabTriggerType } from "./resources/sequences/constants";
export type {
  AutomationCohortVariant,
  AutomationSequenceSummary,
  CollabAutomation,
  CollabAutomationStatus,
  CreatorUpcomingAutomation,
  UpcomingPolicyStep,
} from "./resources/sequences/repo";
export type { CreatorTriggerType } from "./resources/sequences/triggers";
export { TABLE_KEYS, type TableKey } from "./resources/userPreferences/constants";
export type * from "./response";
export { CREATOR_USERNAME_COLUMN } from "./utils/creatorCsv";
export { type ConversionRates, convertCurrency } from "./utils/currency";
export {
  ASSURANCE_VIDEOS_NEGATABLE_FILTERS,
  CANDIDATE_ATTRIBUTE_NEGATABLE_FILTERS,
  CANDIDATES_NEGATABLE_FILTERS,
  CREATORS_NEGATABLE_FILTERS,
  PRODUCTS_NEGATABLE_FILTERS,
  RECOMMENDATIONS_NEGATABLE_FILTERS,
  SAMPLES_NEGATABLE_FILTERS,
  SEQUENCE_RUNS_NEGATABLE_FILTERS,
  VIDEO_NEGATABLE_FILTERS,
} from "./utils/negatable-filters";
export type { Sort } from "./utils/sorting";
export const client = (url: string, options?: ClientRequestOptions) => hc<App>(url, options);

// API-derived response shapes — the single source of truth for these payloads,
// inferred from the typed routes so a route change can't silently drift the
// frontend. Consumed in the frontend via @unbox/api.
type ApiClient = ReturnType<typeof client>;
export type MerchantRiskCatalogResponse = InferResponseType<
  ApiClient["merchant"][":merchantId"]["risks"]["catalog"]["$get"]
>;
export type MerchantRiskCatalogItem = MerchantRiskCatalogResponse[number];
export type WorkspaceRiskCatalogResponse = InferResponseType<
  ApiClient["workspace"][":workspaceId"]["risks"]["catalog"]["$get"]
>;
export type WorkspaceRiskCatalogItem = WorkspaceRiskCatalogResponse[number];
export type WorkspaceRisksListResponse = InferResponseType<ApiClient["workspace"][":workspaceId"]["risks"]["$get"]>;
export type WorkspaceRiskListItem = WorkspaceRisksListResponse[number];
export type WorkspaceSafetyPolicyTemplatesResponse = InferResponseType<
  ApiClient["workspace"][":workspaceId"]["safety-policy-templates"]["$get"]
>;
export type WorkspaceSafetyPolicyTemplateItem = WorkspaceSafetyPolicyTemplatesResponse[number];
export type WorkspacePolicyTemplateStep = WorkspaceSafetyPolicyTemplateItem["steps"][number];
export type WorkspaceQuarantineSettingsResponse = InferResponseType<
  ApiClient["workspace"][":workspaceId"]["quarantine-settings"]["$get"]
>;
export type WorkspaceQuarantineSettingsInput = InferRequestType<
  ApiClient["workspace"][":workspaceId"]["quarantine-settings"]["$put"]
>["json"];
export type SafetyPoliciesResponse = InferResponseType<ApiClient["merchant"][":merchantId"]["safety-policies"]["$get"]>;
export type ActiveJobsResponse = InferResponseType<ApiClient["merchant"][":merchantId"]["jobs"]["active"]["$get"]>;
export type JobResponse = ActiveJobsResponse[number];
export type JobFailuresResponse = InferResponseType<
  ApiClient["merchant"][":merchantId"]["jobs"][":jobId"]["failures"]["$get"]
>;
export type SafetyPolicy = SafetyPoliciesResponse[number];
export type MerchantDetailsSettings = InferResponseType<
  ApiClient["merchant"][":merchantId"]["merchant-details"]["$get"]
>;
export type MerchantCreatorAccount = MerchantDetailsSettings["creators"][number];
export type SampleRulesResponse = InferResponseType<ApiClient["merchant"][":merchantId"]["samples"]["rules"]["$get"]>;
export type SampleRule = SampleRulesResponse["items"][number];
export type SampleExemptionsResponse = InferResponseType<
  ApiClient["merchant"][":merchantId"]["samples"]["rules"]["exemptions"]["$get"]
>;
export type SampleExemption = SampleExemptionsResponse["items"][number];
export type RulesResponse = InferResponseType<ApiClient["merchant"][":merchantId"]["rules"]["$get"]>;
export type Rule = RulesResponse["items"][number];
/** Carries the id of the user an admin is acting as. Sent alongside the admin's own token. */
export const IMPERSONATION_HEADER = "X-Impersonate-User-Id";
/** Carries the caller's feature flag overrides as JSON, where PostHog does not resolve flags. */
export const FEATURE_FLAGS_HEADER = "X-Feature-Flags";
export const SSO_PROVIDER_ID = "workos";
export const SSO_ERRORS = {
  notConfigured: "sso_not_configured",
  jitDisabled: "jit_disabled",
  provisioningFailed: "provisioning_failed",
  accountDeactivated: "account_deactivated",
  required: "SSO_REQUIRED",
} as const;
export { TERMS_OF_SERVICE_URL, WEBSITE_URL } from "@unbox/email/links";
export type ImpersonationTargetsResponse = InferResponseType<ApiClient["admin"]["impersonation"]["users"]["$get"]>;
export type ImpersonationTarget = ImpersonationTargetsResponse[number];
export type ImpersonationTargetWorkspace = ImpersonationTarget["workspaces"][number];
export type ImpersonationTargetMerchant = ImpersonationTargetWorkspace["merchants"][number];
export type ImpersonationMerchantRole = ImpersonationTargetMerchant["role"];
export type SsoDomainStatus = InferResponseType<ApiClient["sso"]["check-domain"]["$get"]>;
export type ScenarioOptions = InferResponseType<ApiClient["admin"]["scenarios"]["options"]["$get"]>;
export type ScenarioTemplateOption = ScenarioOptions["templates"][number];
export type ScenarioSeedOption = ScenarioOptions["scenarios"][number];
export type ScenarioRun = InferResponseType<ApiClient["admin"]["scenarios"]["$get"]>[number];
export type SavedScenarioConfig = InferResponseType<ApiClient["admin"]["scenarios"]["configs"]["$get"]>[number];
export type ScenarioConfigInput = InferRequestType<ApiClient["admin"]["scenarios"]["$post"]>["json"]["config"];
export type BulkReviewPayload = InferRequestType<
  ApiClient["merchant"][":merchantId"]["assurance"]["review"]["bulk"]["$post"]
>["json"];

// Sort columns each paginated route accepts, inferred from its query validator so
// a caller cannot ask for a column the route rejects.
type SortColumn<Query> = NonNullable<Query extends { column?: infer Column } ? Column : never>;
export type TargetCollaborationsSortColumn = SortColumn<
  InferRequestType<ApiClient["merchant"][":merchantId"]["collaborations"]["target"]["$get"]>["query"]
>;
export type OpenCollaborationsSortColumn = SortColumn<
  InferRequestType<ApiClient["merchant"][":merchantId"]["collaborations"]["open"]["$get"]>["query"]
>;
export type TargetCollaborationCreatorsSortColumn = SortColumn<
  InferRequestType<
    ApiClient["merchant"][":merchantId"]["collaborations"]["target"][":collaborationId"]["creators"]["$get"]
  >["query"]
>;
export type OpenCollaborationCreatorsSortColumn = SortColumn<
  InferRequestType<
    ApiClient["merchant"][":merchantId"]["collaborations"]["open"][":collaborationId"]["creators"]["$get"]
  >["query"]
>;
export type SequencesSortColumn = SortColumn<
  InferRequestType<ApiClient["merchant"][":merchantId"]["sequences"]["$get"]>["query"]
>;
export type SequenceRunsSortColumn = SortColumn<
  InferRequestType<ApiClient["merchant"][":merchantId"]["sequences"]["runs"]["$get"]>["query"]
>;
export type SampleRequestGroupsSortColumn = SortColumn<
  InferRequestType<ApiClient["merchant"][":merchantId"]["samples"]["groups"]["$get"]>["query"]
>;
export type SampleRequestGroupItemsSortColumn = SortColumn<
  InferRequestType<ApiClient["merchant"][":merchantId"]["samples"]["group-items"]["$get"]>["query"]
>;

export type {
  AdsAuthorisationAdvertiser,
  Metric,
  OverviewMetricKey,
  OverviewTotalsCurrency,
  SetupChecklistItem,
} from "@unbox/db";

export { type ClientErrorKind, type ClientErrorPayload, ClientErrorSchema } from "./errors";
export {
  type FeatureFlagOverrides,
  type PosthogFeatureFlag,
  parseFeatureFlagOverrides,
  posthogFeatureFlags,
  type RemoteConfigKey,
  remoteConfigKeys,
  UNSET_FEATURE_FLAG,
} from "./features";
export type { InvitationSort } from "./resources/admin/invitations/repo";
export type {
  ConfigFieldDescriptor,
  ConfigFieldKey,
  SUPPORTED_CONFIG_FIELD_TYPES,
} from "./resources/admin/risks/config-schemas";
export {
  extractPlaceholders,
  MERCHANT_RISK_CONFIG_FIELDS,
  PLACEHOLDER_VALIDATED_FIELDS,
  RISK_CONFIG_FIELDS,
} from "./resources/admin/risks/config-schemas";
export type {
  CreatorCohortBatchPayload,
  CreatorCohortImportError,
  CreatorCohortImportPreviewRow,
  CreatorCohortImportResult,
} from "./resources/creatorCohorts/routes";
export type {
  ActivitiesResponse,
  CollabJoinActivity,
  CollabRemovalActivity,
  CreatorActivity,
  MessageActivity,
  SafetyEventActivity,
  VideoPostActivity,
} from "./resources/creators/types";
export type {
  CreatorTagBatchPayload,
  CreatorTagImportError,
  CreatorTagImportResult,
} from "./resources/creatorTags/routes";
export type { GmvBreakdownLens } from "./resources/home/repo";
export { MAX_MESSAGE_TEMPLATE_LENGTH } from "./resources/messageTemplates/constants";
export type { MetricReturnType } from "./resources/metrics/repo";
export type { DisplayRole, EffectiveRole, PermissionUser } from "./resources/permissions/permissions";
export {
  canAccessWorkspaceArea,
  canAssignRole,
  canConfigureSafety,
  deriveDisplayRole,
  getEffectiveMerchantRole,
  getEffectiveWorkspaceRole,
  invitableRoles,
  merchantScopeRoles,
  workspaceScopeRoles,
} from "./resources/permissions/permissions";
export type {
  ActiveCreatorsOverview,
  OverviewMerchant,
  OverviewSeriesPoint,
  OverviewValues,
  PortfolioOverview,
} from "./resources/portfolio/overview-repo";
export type {
  MerchantRecommendationsResponse,
  MerchantRecommendedCreator,
  RecommendationsCountResponse,
  RecommendationsResponse,
  RecommendedCreator,
} from "./resources/recommendations/repo";
export { SAFETY_POLICY_CONFIG, type SafetyPolicySeed } from "./resources/safetyPolicies/config";
export {
  SAFETY_POLICY_GMV_MAX_ONLY,
  SAFETY_POLICY_LABELS,
  SAFETY_POLICY_PRIORITY,
  SAFETY_POLICY_TYPES,
} from "./resources/safetyPolicies/constants";

export {
  type ComparisonOperator as SampleRuleComparisonOperator,
  type Condition as SampleRuleCondition,
  type ConditionGroup as SampleRuleConditionGroup,
  FIELD_DEFINITIONS as SAMPLE_RULE_FIELD_DEFINITIONS,
  type FieldId as SampleRuleFieldId,
  type Operator as SampleRuleOperator,
  type RuleConditions as SampleRuleConditions,
  ruleConditionsSchema as sampleRuleConditionsSchema,
  type TagOperator as SampleRuleTagOperator,
} from "./resources/samples/conditions";
export type {
  CreateSampleRulePayload,
  RuleAction as SampleRuleAction,
  UpdateSampleRulePayload,
} from "./resources/samples/routes";

export { MANUAL_SETUP_CHECKLIST_ITEMS, SETUP_CHECKLIST_ITEMS } from "./resources/setupChecklist/constants";

export type { SmartBriefWithContent } from "./resources/smartBriefs/repo";
export {
  allTemplateTokens,
  findTokensOutsideContext,
  findUnknownTokens,
  renderTemplate,
  TEMPLATE_TOKENS,
  type TemplateContext,
  type TemplateTokenBase,
  type TemplateTokenContext,
  templateTokensForContext,
} from "./resources/tiktokShop/templateVariables";
export type { VideoExplorerFilters, VideoSelection } from "./resources/video-selection";
export { type Event, type FailedCreator, LIFECYCLE_EVENTS } from "./services/pubsub/types";
export type { Message, StreamedMessage } from "./services/sse/connectionManager";
export {
  isSeaCountry,
  SEA_COUNTRIES,
  type SeaCountry,
  TIKTOK_REGIONS,
  type TikTokMessage,
  type TikTokRegion,
} from "./services/tts/types";
export {
  CONFIG_KEY_TO_FLAG_OVERRIDES,
  configKeyToDisplayRiskType,
  configKeyToFlagRiskType,
  mapRiskTypeShort,
  type RiskType,
} from "./utils/riskTypeMapping";
